import { Children, cloneElement, Fragment, ReactElement } from 'react'
import Styles from './Stepper.module.scss'
import { StepperProps } from './Types'
import { Step } from './Step'
import { Divider } from './Divider'
import cx from 'classnames'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

const Root = ({ current, variant = 'blue', completedAll, hiddenStepIndexes, children }: StepperProps) => {
  const childrenArray = Children.toArray(children) as ReactElement[]
  const stepIndexes = Array.from({ length: childrenArray.length }, (_, i) => i + 1)
  const visibleIndexes = stepIndexes.filter((i) => !hiddenStepIndexes?.includes(i))
  const isDesktopWizardsUpdatesOn = useFlipperFlag('desktop_wizards_updates')
  const { isMobile } = useResponsiveDesign()

  // If current step is visible, return its position in the array of visible steps
  // If current step is hidden, return the position of the biggest visible step that comes before the current step
  const actualCurrent =
    visibleIndexes.indexOf(current) + 1 ||
    visibleIndexes.indexOf(Math.max(...visibleIndexes.filter((i) => i < current))) + 1

  return (
    <div className={cx(Styles.stepper, Styles[variant])}>
      {childrenArray.map((child, i) => {
        const position = i + 1

        if (hiddenStepIndexes?.includes(position)) return null

        // Offset: amount of hidden steps that come before the current step
        const offset = hiddenStepIndexes?.filter((hiddenIndex) => position > hiddenIndex).length || 0
        const offsetPosition = position - offset

        return (
          <Fragment key={`step-${i}`}>
            {cloneElement(child, {
              ...child.props,
              position: offsetPosition,
              active: !completedAll && actualCurrent === offsetPosition,
              completed: completedAll || actualCurrent > offsetPosition,
            })}
            {i + 1 < childrenArray.length && (isMobile || !isDesktopWizardsUpdatesOn) && <Divider />}
          </Fragment>
        )
      })}
    </div>
  )
}

export const Stepper = {
  Root,
  Step,
}
